import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/joy";
import { LinkOptions } from "@tanstack/react-router";
import { ApiChallenge } from "griptonite-api-client";
import { ReactNode, useEffect, useMemo, useState } from "react";

import { ButtonLink } from "../Links";
import getPublishWarnings from "./getPublishWarnings";

const BottomButtons = ({
  challenge,
  nextLink,
  prevLink,
  formState,
  isFinalStep,
  onPublishClick,
}: {
  challenge?: ApiChallenge;
  nextLink?: LinkOptions;
  prevLink?: LinkOptions;
  formState?: { isSubmitting: boolean; isDirty: boolean };
  isFinalStep?: boolean;
  onPublishClick?: () => void;
}) => {
  const isCreatingChallenge = !challenge || challenge.isHidden;
  const isWizardMode = isCreatingChallenge;

  const publishWarnings = useMemo(() => {
    if (challenge && isFinalStep) {
      return getPublishWarnings(challenge);
    }
    return undefined;
  }, [challenge, isFinalStep]);

  const [didClickPublish, setDidClickPublish] = useState(false);
  useEffect(() => {
    if (!formState?.isSubmitting) {
      setDidClickPublish(false);
    }
  }, [formState?.isSubmitting]);

  let nextButton: ReactNode = null;
  if (
    (!isFinalStep || !isWizardMode) &&
    (formState?.isDirty || (formState && (!isWizardMode || !nextLink)))
  ) {
    nextButton = (
      <Button
        type="submit"
        size="lg"
        color="primary"
        loading={formState.isSubmitting}
        loadingPosition="start"
        endDecorator={isWizardMode ? <KeyboardArrowRight /> : null}
        disabled={!formState.isDirty}
      >
        {isWizardMode ? "Save and Continue" : "Save"}
      </Button>
    );
  } else if (isFinalStep) {
    nextButton = (
      <>
        <Button
          name="publish"
          value="false"
          type="submit"
          size="lg"
          color="primary"
          variant="outlined"
          disabled={!formState?.isDirty}
          loading={formState?.isSubmitting && !didClickPublish}
          loadingPosition="start"
          sx={{ marginRight: 1.5 }}
        >
          Save without publishing
        </Button>
        <Button
          type="button"
          name="publish"
          value="true"
          size="lg"
          color="primary"
          loading={formState?.isSubmitting && didClickPublish}
          loadingPosition="start"
          disabled={publishWarnings && publishWarnings?.allWarnings.length > 0}
          onClick={() => {
            if (onPublishClick) {
              onPublishClick();
            }
            setDidClickPublish(true);
          }}
        >
          Publish
        </Button>
      </>
    );
  } else if (nextLink && isWizardMode) {
    nextButton = (
      <ButtonLink
        replace
        size="lg"
        color="primary"
        endDecorator={<KeyboardArrowRight />}
        // oxlint-disable-next-line typescript/no-explicit-any
        {...nextLink}
      >
        Continue
      </ButtonLink>
    );
  }

  return (
    <Stack direction="row" alignItems="center">
      {prevLink && isWizardMode ? (
        <ButtonLink
          replace
          size="lg"
          color="neutral"
          startDecorator={<KeyboardArrowLeft />}
          // oxlint-disable-next-line typescript/no-explicit-any
          {...prevLink}
        >
          Back
        </ButtonLink>
      ) : null}
      <Box sx={{ flex: 1 }} />
      {nextButton}
    </Stack>
  );
};

export default BottomButtons;
